import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  List,
  Switch,
  Divider,
  Card,
  useTheme,
  Button,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
  selectNotificationPreferences,
  selectNotificationUpdating,
} from '../store/slices/notificationSlice';
import { Bell, Mail, Heart, Users, Star, Megaphone } from 'lucide-react-native';
import Toast from 'react-native-toast-message';
import { logger } from '../utils/logger';

export function NotificationSettingsScreen() {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const preferences = useAppSelector(selectNotificationPreferences);
  const isUpdating = useAppSelector(selectNotificationUpdating);

  useEffect(() => {
    dispatch(fetchNotificationPreferences());
  }, [dispatch]);

  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const RATE_LIMIT_MS = 1000; // 1 second between updates

  const handleToggle = async (key: string, value: boolean) => {
    const now = Date.now();
    if (now - lastUpdateTime < RATE_LIMIT_MS) {
      Toast.show({
        type: 'info',
        text1: 'Please wait',
        text2: 'Too many requests. Please wait a moment.',
      });
      return;
    }

    try {
      setLastUpdateTime(now);
      await dispatch(updateNotificationPreferences({ [key]: value })).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Notification settings updated',
      });
    } catch (error: any) {
      logger.error('Failed to update notification settings:', error);
      Toast.show({
        type: 'error',
        text1: 'Failed to update settings',
        text2: error?.message || 'Please check your connection and try again',
      });
    }
  };

  const notificationTypes = [
    {
      key: 'push_notifications',
      title: 'Push Notifications',
      description: 'Receive notifications on your device',
      icon: Bell,
    },
    {
      key: 'email_notifications',
      title: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: Mail,
    },
    {
      key: 'recipe_likes',
      title: 'Recipe Likes',
      description: 'When someone likes your recipe',
      icon: Heart,
    },
    {
      key: 'recipe_comments',
      title: 'Recipe Comments',
      description: 'When someone comments on your recipe',
      icon: Star,
    },
    {
      key: 'new_followers',
      title: 'New Followers',
      description: 'When someone follows you',
      icon: Users,
    },
    {
      key: 'creator_updates',
      title: 'Creator Updates',
      description: 'Updates from creators you follow',
      icon: Megaphone,
    },
    {
      key: 'system_announcements',
      title: 'System Announcements',
      description: 'Important updates and announcements',
      icon: Bell,
    },
    {
      key: 'marketing_emails',
      title: 'Marketing Emails',
      description: 'Promotional content and newsletters',
      icon: Mail,
    },
  ];

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.title, { color: theme.colors.onSurface }]}
            >
              Notification Preferences
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.description,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Choose which notifications you want to receive
            </Text>

            {notificationTypes.map((item, index) => (
              <React.Fragment key={item.key}>
                <List.Item
                  title={item.title}
                  description={item.description}
                  left={props => (
                    <item.icon
                      {...props}
                      size={24}
                      color={theme.colors.onSurfaceVariant}
                    />
                  )}
                  right={() => (
                    <Switch
                      value={
                        preferences?.[item.key as keyof typeof preferences] ||
                        false
                      }
                      onValueChange={value => handleToggle(item.key, value)}
                      disabled={isUpdating}
                    />
                  )}
                  style={styles.listItem}
                />
                {index < notificationTypes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Card.Content>
        </Card>

        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text
              variant='titleMedium'
              style={[styles.title, { color: theme.colors.onSurface }]}
            >
              Quick Actions
            </Text>

            <Button
              mode='outlined'
              onPress={() => {
                // Enable all notifications
                const allEnabled = notificationTypes.reduce((acc, item) => {
                  acc[item.key] = true;
                  return acc;
                }, {} as any);

                dispatch(updateNotificationPreferences(allEnabled));
              }}
              style={styles.actionButton}
              disabled={isUpdating}
            >
              Enable All
            </Button>

            <Button
              mode='outlined'
              onPress={() => {
                // Disable all notifications except system announcements
                const allDisabled = notificationTypes.reduce((acc, item) => {
                  acc[item.key] = item.key === 'system_announcements';
                  return acc;
                }, {} as any);

                dispatch(updateNotificationPreferences(allDisabled));
              }}
              style={styles.actionButton}
              disabled={isUpdating}
            >
              Disable All (Keep System)
            </Button>
          </Card.Content>
        </Card>

        <Card
          style={[
            styles.infoCard,
            { backgroundColor: theme.colors.surfaceVariant },
          ]}
        >
          <Card.Content>
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              💡 Tip: You can always change these settings later. System
              announcements are recommended to stay informed about important
              updates.
            </Text>
          </Card.Content>
        </Card>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  infoCard: {
    marginBottom: 16,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  description: {
    marginBottom: 16,
  },
  listItem: {
    paddingVertical: 8,
  },
  actionButton: {
    marginBottom: 8,
  },
});
